import React, { useCallback, useMemo, useState } from "react";

import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { Column, Row } from "components/Layout";
import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import { SideLoadedBusinessColumnDefs } from "constants/columnDefinitions/sideLoadedBusiness";

import { useHasExportBusinessesPermission } from "hooks";

import { openCreateSideLoadedBusinessModal } from "lib/navigation";

import { selectSideLoadedBusinessesList } from "selectors";

import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

const additionalActions = [
  {
    default: true,
    // TODO - implement uploading a Legacy Business data set.
    title: "Upload Data Set",
    isDisabled: true,
    icon: faUpload,
    dataTour: "uploadSideLoadedDataSet",
  },
];

export function SideLoadedBusinessManageTable(): React.JSX.Element {
  const rowData = useSelector(selectSideLoadedBusinessesList);
  const hasExportCsvPermissions = useHasExportBusinessesPermission();

  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const selectedRecordCount = selectedRecordIds.length;

  const onRowSelectionChange = useCallback(
    (rows: SideLoadedBusiness[]) => {
      const newSelectedRecordIds = rows.map(row => row.id);
      if (
        newSelectedRecordIds.length !== selectedRecordCount ||
        !newSelectedRecordIds.every(id => selectedRecordIds.includes(id))
      ) {
        setSelectedRecordIds(newSelectedRecordIds);
      }
    },
    [selectedRecordCount, selectedRecordIds],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      onlySelected: !!selectedRecordCount,
    }),
    [selectedRecordCount],
  );

  const handleSelectAndCompare = useCallback((recordId: string) => {
    openCreateSideLoadedBusinessModal(undefined, recordId);
  }, []);

  const context = useSideLoadedBusinessGridContext(handleSelectAndCompare);

  return (
    <Column fullHeight>
      {rowData.length === 0 && (
        <Row paddingVertical={2}>
          <SmallText>
            No Legacy Business data has been uploaded yet. Once a data set has
            been uploaded, its businesses will be listed here.
          </SmallText>
        </Row>
      )}
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={SideLoadedBusinessColumnDefs}
        context={context}
        getRowId={getSideLoadedBusinessRowId}
        rowData={rowData}
        tableName={AgGridTables.SIDELOADED_BUSINESSES_MANAGE}
        hasExportCsvPermissions={hasExportCsvPermissions}
        exportSaleTitle="Legacy Businesses"
        defaultCsvExportParams={defaultCsvExportParams}
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection={RowSelectionMode.MULTIPLE}
        rowsSelectable
      />
    </Column>
  );
}
